import React, { useState, useEffect } from 'react';
import { queryFamilyForDonation, createEventDonation } from "../../service/DonationApi";
import { Helmet } from "react-helmet-async";
import { Navbar, Footer, Loading, Breadcrumbs } from "../../components/shared";
import { useCheckAccess } from "../../utils/Utilities";
import { useNavigate } from "react-router-dom";
import { getEventById } from "../../service/EventsApi";

// Function to get today's date
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Main component for donation creation
const EventDonationCreate = () => {
  useCheckAccess('EventsRW');

  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [familyId, setFamilyId] = useState('');
  const [mobile, setMobile] = useState('');
  const [aadhar, setAadhar] = useState('');
  const [donationDate, setDonationDate] = useState(getTodayDate());
  const [loading, setLoading] = useState(false);

  const [searchResult, setSearchResult] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [searchError, setSearchError] = useState('');
  const [validationError, setValidationError] = useState('');
  const navigate = useNavigate();
  const [searching, setSearching] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [manualDetails, setManualDetails] = useState({
    name: '',
    mobile: '',
    aadhar: '',
    area: '',
    village: '',
    taluk: '',
    district: '',
    pin: ''
  });


  const queryParams = new URLSearchParams(window.location.search);
  const eventId = queryParams.get("eventId");
  const [event, setEvent] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) return;
      try {
        const eventData = await getEventById(eventId);

        if (
          !eventData ||
          !eventData.start?._seconds ||
          !eventData.end?._seconds
        ) {
          console.warn("Invalid event data received:", eventData);
          alert("நிகழ்வு விவரங்கள் தவறாக உள்ளன.");
          return;
        }

        const startDate = new Date(eventData.start._seconds * 1000);
        const endDate = new Date(eventData.end._seconds * 1000);

        if (eventData?.title && eventData?.start) {
          setSelectedSubcategories([{
            subcategory: eventData.id,
            amount: '',
            //transactionId: familyId + '_' + eventData.id,
          }]);
          setEvent({
            ...eventData,
            startDate,
            endDate
          });
        } else {
          alert("Event not found");
        }
      } catch (error) {
        console.error("Failed to fetch event:", error);
        alert("Failed to fetch event details");
      }
    };

    fetchEvent();
  }, [eventId]);

  const selectedMember = searchResult?.members?.find(m => m.aadhar === selectedMemberId);

  const isValidSearchInput = () => {
    if (!familyId && !mobile && !aadhar) {
      setValidationError("தயவுசெய்து குடும்ப எண், Mobile அல்லது Aadhaar ஒன்றை உள்ளிடவும்.");
      return false;
    }

    if (familyId && !/^\d{1,4}$/.test(familyId)) {
      setValidationError("குடும்ப எண் 1 முதல் 4 இலக்க எண் இருக்க வேண்டும்.");
      return false;
    }

    if (mobile && !/^\d{10}$/.test(mobile)) {
      setValidationError("மொபைல் எண் 10 இலக்க எண் இருக்க வேண்டும்.");
      return false;
    }

    if (aadhar && !/^\d{12}$/.test(aadhar)) {
      setValidationError("ஆதார் எண் 12 இலக்க எண் இருக்க வேண்டும்.");
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleSearchFamily = async () => {
    setSearchResult(null);
    setSearchError('');
    setSelectedMemberId('');
    setManualEntry(false);
    setSearching(true);

    if (!isValidSearchInput()) {
      setSearching(false);
      return;
    }

    try {
      const response = await queryFamilyForDonation({ familyId, mobile, aadhar });


      if (response?.family || (response?.members?.length > 0)) {
        setSearchResult(response);
        if (response.family?.id) {
          setFamilyId(response.family.id);
        }
      } else {
        setSearchError("No matching family found. Would you like to enter details manually?");
        setFamilyId(null);
        setAadhar(null);
        setMobile(null);
        setManualEntry(true);

      }
    } catch (err) {
      setSearchError("Error fetching family data.");
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const handleAddDonation = async () => {
    if (manualEntry) {
      const { name, mobile } = manualDetails;

      if (!name.trim() || !mobile.trim()) {
        alert("தயவுசெய்து பெயர் மற்றும் மொபைல் எண்ணை உள்ளிடவும்.");
        return;
      }
      if (!/^\d{10}$/.test(mobile)) {
        alert("மொபைல் எண் 10 இலக்கமாக இருக்க வேண்டும்.");
        return;
      }
    } else if (!selectedMember) {
      alert("தயவுசெய்து ஒரு குடும்ப உறுப்பினரை தேர்வு செய்யவும்.");
      return;
    }


    // ❗ Validate amounts
    for (const sub of selectedSubcategories) {
      if (!sub.amount || isNaN(sub.amount)) {
        alert("தயவுசெய்து அனைத்து துணை வகைகளுக்கும் நன்கொடை தொகையை உள்ளிடவும்.");
        return;
      }
    }
    setLoading(true);
    let response;

    try {
      response = await createEventDonation({
        familyId: manualEntry ? '' : familyId,
        familyMemberId: selectedMember?.aadhar || '',
        familyMemberFirstName: manualEntry ? manualDetails.name : selectedMember.firstName,
        familyMemberMobile: manualEntry
          ? manualDetails.mobile
          : (selectedMember.mobile?.includes('*') ? searchResult.family.familyHeadMobile || "" : selectedMember.mobile) || "",
        familyMemberMobile2: manualEntry
          ? ''
          : (selectedMember.mobile2?.includes('*') ? searchResult.family.familyHeadMobile || "" : selectedMember.mobile2) || "",
        category: event.id,
        categoryDescription: event.title || '',
        date: donationDate,
        subcategories: selectedSubcategories.map(sub => ({
          subcategory: sub.subcategory,
          subCategoryDescription: sub.subcategory, // event flow subcategory description same as subcategory ID
          amount: Number(sub.amount),
          transactionId: sub.transactionId,
        })),
        manualDetails: manualEntry ? manualDetails : undefined,
      });


      if (response === 409) {
        alert("❗ Donation already exists.");
        const queryParams = manualEntry ? `?guestId=${manualDetails.mobile}` : '';
        navigate(`/donation/view/${manualEntry ? 'guest' : familyId}${queryParams}`);
        return;
      }

      if (response) {
        alert("✅ Donation added successfully!");
        const queryParams = manualEntry ? `?guestId=${manualDetails.mobile}` : '';
        navigate(`/donation/view/${manualEntry ? 'guest' : familyId}${queryParams}`);
      }

    } catch (err) {
      console.error("Error adding donation:", err);
      alert("⚠️ Something went wrong while adding the donation.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <Helmet>
        <title>ஸ்ரீ பொன்னாச்சி அம்மன் திருக்கோவில்கள் | Event Donation Create</title>
        <link rel="canonical" href="/donation/create" />
      </Helmet>

      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <Breadcrumbs /><br />
            <h2>நன்கொடை உருவாக்கு</h2>

            <div className="auth_element">
              <label className="auth_label">குடும்ப எண்:</label>
              <input value={familyId} onChange={e => setFamilyId(e.target.value)}
                className="auth_input"
                inputMode="numeric"
                maxLength="4"
              />

              <label className="auth_label">மொபைல்:</label>
              <input value={mobile} onChange={e => setMobile(e.target.value)}
                className="auth_input"
                inputMode="numeric"
                maxLength="10" />

              <label className="auth_label">ஆதார்:</label>
              <input value={aadhar} onChange={e => setAadhar(e.target.value)}
                className="auth_input"
                inputMode="numeric"
                maxLength="14"
              />

              {searching ? (
                <Loading />
              ) : (
                <button onClick={handleSearchFamily}>தேடு</button>
              )}

              {validationError && <p style={{ color: 'red' }}>{validationError}</p>}

            </div>

            {searchError && <p style={{ color: 'red' }}>{searchError}</p>}

            {searchResult?.family && (
              <>

                <div className="detail-item" style={{
                  border: "1px solid var(--highlight-shadow-color)",
                  padding: "1rem",
                  marginTop: "1rem",

                }}>

                  <span className="detail-label"><strong>குடும்ப எண்:</strong></span> <span className="detail-value">{searchResult.family.id}

                  </span>
                  <span className="detail-label"><strong>கூட்டம்:</strong></span><span className="detail-value"> {searchResult.family.kootamName}
                  </span>
                  <span className="detail-label"><strong>தலைமை பெயர்:</strong> </span><span className="detail-value">{searchResult.family.familyHeadFirstName} {searchResult.family.familyHeadLastName}
                  </span>
                  {searchResult.family.tempAddressName_tamil && (
                    <>
                      <span className="detail-label"><strong>தற்காலிக முகவரி:</strong> </span>
                      <span className="detail-value">{searchResult.family.tempAddressName_tamil}, {searchResult.family.tempAddressBlock_tamil}, {searchResult.family.tempAddressDistrict_tamil}
                      </span>
                    </>
                  )}
                  <span className="detail-label"><strong>நிரந்தர முகவரி:</strong> </span>
                  <span className="detail-value">{searchResult.family.permanentAddressName_tamil}, {searchResult.family.permanentAddressBlock_tamil}, {searchResult.family.permanentAddressDistrict_tamil}
                  </span>
                  <br />
                  <button
                    style={{ marginTop: '1rem' }}
                    onClick={() => navigate(`/donation/view/${searchResult.family.id}`)}
                  >
                    குடும்ப நன்கொடைப் பதிவு பார்க்க
                  </button>

                </div>
                {searchResult.family.status !== 'active' && (
                  <div style={{ marginTop: '1rem', color: 'red', fontWeight: 'bold' }}>
                    ⚠️ குடும்ப விவரங்கள் முலுமையாக இல்லை. தயவுசெய்து விண்ணப்பப் படிவத்தை பூர்த்தி செய்யவும்.
                    <br></br>
                  </div>

                )}
              </>
            )}

            {searchResult?.members?.length > 0 && (
              <div className="auth_element" style={{ marginTop: "1rem" }}>
                <h3>குடும்ப உறுப்பினர்கள்:</h3>
                <br />
                {searchResult.members.map(member => (
                  <div key={member.aadhar}>
                    <label className='auth_radio'>
                      <input
                        type="radio"
                        name="selectedMember"
                        value={member.aadhar}
                        checked={selectedMemberId === member.aadhar}
                        onChange={() => setSelectedMemberId(member.aadhar)}
                      />
                      {member.firstName} {member.lastName}{(member.mobile || member.mobile2) ? ` - 📱 ${member.mobile || ''}${member.mobile2 ? (member.mobile ? ' / ' : '') + member.mobile2 : ''}` : ''}
                    </label>
                    {member.status !== 'active' && (
                      <div style={{ color: 'red', marginLeft: '1.5rem', fontSize: '0.9rem' }}>
                        ⚠️ இந்த உறுப்பினர் ஆதார் விவரங்களை சமர்ப்பிக்கவும்.
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            {/* Manual Entry Fallback */}
            {manualEntry && (
              <div style={{ border: "1px solid #ccc", padding: "1.5rem", marginTop: "1.5rem", borderRadius: "8px", background: "#f9f9f9" }}>
                <h3 style={{ marginBottom: "1rem" }}>📝 கைமுறையாக விவரங்களை உள்ளிடுக</h3>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "1rem" }}>
                  <div className="auth_element">
                    <label htmlFor="name">பெயர்: <span style={{ color: "red" }}>*</span></label>
                    <input
                      id="name"
                      type="text"
                      placeholder="பெயர்"
                      required
                      value={manualDetails.name}
                      onChange={(e) => setManualDetails(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>

                  <div className="auth_element">
                    <label htmlFor="mobile">மொபைல்: <span style={{ color: "red" }}>*</span></label>
                    <input
                      id="mobile"
                      type="tel"
                      placeholder="10 இலக்க எண்"
                      required
                      maxLength="10"
                      value={manualDetails.mobile}
                      onChange={(e) => setManualDetails(prev => ({ ...prev, mobile: e.target.value }))}
                    />
                  </div>

                  <div className="auth_element">
                    <label htmlFor="aadhar">ஆதார்:</label>
                    <input
                      id="aadhar"
                      type="text"
                      placeholder="12 இலக்க ஆதார்"
                      maxLength="12"
                      value={manualDetails.aadhar}
                      onChange={(e) => setManualDetails(prev => ({ ...prev, aadhar: e.target.value }))}
                    />
                  </div>

                  <div className="auth_element">
                    <label htmlFor="village">ஊர்:</label>
                    <input
                      id="village"
                      type="text"
                      placeholder="ஊர் பெயர்"
                      value={manualDetails.village}
                      onChange={(e) => setManualDetails(prev => ({ ...prev, village: e.target.value }))}
                    />
                  </div>

                  <div className="auth_element">
                    <label htmlFor="area">அஞ்சல்:</label>
                    <input
                      id="area"
                      type="text"
                      placeholder="அஞ்சல்"
                      value={manualDetails.area}
                      onChange={(e) => setManualDetails(prev => ({ ...prev, area: e.target.value }))}
                    />
                  </div>

                  <div className="auth_element">
                    <label htmlFor="taluk">தாலுகா:</label>
                    <input
                      id="taluk"
                      type="text"
                      placeholder="தாலுகா"
                      value={manualDetails.taluk}
                      onChange={(e) => setManualDetails(prev => ({ ...prev, taluk: e.target.value }))}
                    />
                  </div>

                  <div className="auth_element">
                    <label htmlFor="district">மாவட்டம்:</label>
                    <input
                      id="district"
                      type="text"
                      placeholder="மாவட்டம்"
                      value={manualDetails.district}
                      onChange={(e) => setManualDetails(prev => ({ ...prev, district: e.target.value }))}
                    />
                  </div>

                  <div className="auth_element">
                    <label htmlFor="pin">பின் குறியீடு:</label>
                    <input
                      id="pin"
                      type="text"
                      placeholder="PIN Code"
                      maxLength="6"
                      value={manualDetails.pin}
                      onChange={(e) => setManualDetails(prev => ({ ...prev, pin: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            )}

            {(selectedMember || manualEntry) && (
              <>
                <>
                  <div className="auth_element" style={{ marginTop: "1rem" }}>
                    <h3>🎉 நிகழ்வு விவரங்கள்</h3>
                    <p><strong>தலைப்பு:</strong> {event.title}</p>
                    <p><strong>தேதி:</strong> {new Date(event.startDate).toLocaleDateString('ta-IN')}</p>
                    <p><strong>விளக்கம்:</strong> {event.description || '—'}</p>
                  </div>
                </>

                {eventId && selectedSubcategories.length > 0 && (
                  <div className="auth_element">
                    <label>நன்கொடை தொகை:</label>
                    <input
                      type="number"
                      value={selectedSubcategories[0].amount}
                      onChange={(e) => {
                        const amount = e.target.value;
                        setSelectedSubcategories(prev => [
                          { ...prev[0], amount: parseFloat(amount) || '' }
                        ]);
                      }}
                    />
                  </div>
                )}

                <div className="auth_element">
                  <label>நன்கொடை தேதி:</label>
                  <input
                    type="date"
                    value={donationDate}
                    onChange={e => setDonationDate(e.target.value)}
                  />
                </div>

                <div className="auth_element" style={{ marginTop: "1rem" }}>
                  {loading ? (
                    <Loading />
                  ) : (
                    <button onClick={handleAddDonation}>
                      நன்கொடைச் சேர்க்கவும்
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div >

      <Footer />
    </>
  );
};

export default EventDonationCreate;
